import { useCallback, useRef } from 'react';

import { twMerge } from 'tailwind-merge';

import type { InputProps, SelectOption } from '@/_components';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Input, Label } from '@/_components';
import { Button } from '@/_components/button';
import { stripHtml, useHighlightedText } from '@/_shared';

import { insertAtSelection } from './insert-at-selection';
import { VariableInsertIcon } from './variable-insert-icon';

type TextSelection = { start: number; end: number };

export type VariableTextInputProps = Omit<InputProps, 'value' | 'onChange' | 'postSlot'> & {
  value: string;
  onChange: (value: string) => void;
  variableOptions?: SelectOption<string>[];
};

export function VariableTextInput({
  value,
  onChange,
  variableOptions = [],
  disabled,
  label,
  className,
  ...props
}: VariableTextInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const selectionRef = useRef<TextSelection>({ start: 0, end: 0 });
  const displayValue = stripHtml(value);
  const highlightedHtml = useHighlightedText(displayValue);
  const showVariableMenu = !disabled && variableOptions.length > 0;

  const saveSelection = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;
    selectionRef.current = {
      start: input.selectionStart ?? 0,
      end: input.selectionEnd ?? 0,
    };
  }, []);

  const insertVariable = useCallback(
    (token: string) => {
      const input = inputRef.current;
      const current = displayValue;
      const { start, end } = selectionRef.current;
      const next = insertAtSelection(current, token, start, end);
      onChange(next);
      requestAnimationFrame(() => {
        if (input) {
          input.focus();
          const cursor = start + token.length;
          input.setSelectionRange(cursor, cursor);
          selectionRef.current = { start: cursor, end: cursor };
        }
      });
    },
    [displayValue, onChange]
  );

  if (disabled) {
    return (
      <div className={twMerge('flex flex-col gap-2', className)}>
        {label ? <Label>{label}</Label> : null}
        <div
          className="border-c-border text-c-card-foreground min-h-11 border-b py-2.5 pl-0.5 text-sm leading-normal"
          dangerouslySetInnerHTML={{ __html: highlightedHtml || '&nbsp;' }}
        />
      </div>
    );
  }

  return (
    <div className={className}>
      <Input
        {...props}
        ref={inputRef}
        label={label}
        value={displayValue}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onSelect={saveSelection}
        onKeyUp={saveSelection}
        onClick={saveSelection}
        postSlot={
          showVariableMenu ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  tag="button"
                  size="sm"
                  as="link"
                  icon={VariableInsertIcon}
                  aria-label="Insert placeholder"
                  className="text-accent hover:text-accent opacity-40 hover:bg-black/10 hover:opacity-100 dark:hover:bg-black/10 dark:hover:text-white"
                  onPointerDown={saveSelection}
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-48">
                {variableOptions.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    onSelect={(event) => {
                      event.preventDefault();
                      insertVariable(option.value);
                    }}
                  >
                    {option.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : undefined
        }
      />
    </div>
  );
}
